import React, { useEffect, useState, useRef, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, AlertCircleIcon, ShieldIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import * as Sentry from '@sentry/react';
import { isDisposableEmail } from '../utils/validationUtils';
// ─── Cloudflare Turnstile config ────────────────────────────────────────
// Feature flag: flip to `false` to disable the CAPTCHA gate entirely
// (e.g. emergency rollback) without removing any code. The widget will
// not render and handleSubmit will skip the verifyTurnstile call.
const ENABLE_SIGNUP_CAPTCHA = true;
// Cloudflare Turnstile PUBLIC site key. Safe to commit.
// The SECRET key lives in Firebase Secret Manager (TURNSTILE_SECRET_KEY)
// and is only readable by the verifyTurnstile Cloud Function.
const TURNSTILE_SITE_KEY = '0x4AAAAAADYnh4MLjOOURJjN';
// Cloudflare's Turnstile JS API URL. Loaded once per page via <script>.
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
// ─── Signup-velocity telemetry helper ───────────────────────────────────
// Fires the recordSignupOutcome Cloud Function in a fire-and-forget way.
// TELEMETRY ONLY — this function MUST NEVER throw to the caller and MUST
// NEVER affect the signup outcome. See SIGNUP_VELOCITY_TELEMETRY_DIAGNOSIS.md.
type ClientSignupOutcome = 'signup_succeeded' | 'signup_auth_error' | 'signup_disposable_email';
const fireSignupTelemetry = (outcome: ClientSignupOutcome, errorCode?: string): void => {
  try {
    const callable = httpsCallable<{
      flow: 'student';
      outcome: ClientSignupOutcome;
      errorCode?: string;
    }, {
      recorded: boolean;
    }>(functions, 'recordSignupOutcome');
    callable({
      flow: 'student',
      outcome,
      errorCode
    }).catch(() => {
      // Swallow — telemetry failures are never surfaced to the user.
    });
    Sentry.addBreadcrumb({
      category: 'signup',
      level: outcome === 'signup_succeeded' ? 'info' : 'warning',
      message: outcome,
      data: {
        flow: 'student',
        errorCode
      }
    });
  } catch {
    // Swallow — telemetry must never break the signup flow.
  }
};
// Tell TypeScript that `window.turnstile` exists at runtime once the
// Cloudflare script has loaded.
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement | string, params: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}
export const StudentSignUpFormSection = () => {
  const navigate = useNavigate();
  const {
    registerStudent
  } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  // ─── Turnstile state ───────────────────────────────────────────────────
  // Holds the most recent Turnstile token. null when the widget hasn't
  // resolved yet, when it expires, or when verification just failed.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // DOM container where Cloudflare mounts the widget iframe.
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  // Widget id returned by window.turnstile.render — used to reset the
  // widget after a failed verification.
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // PHASE 3: Real-time field validation
  const [fieldErrors, setFieldErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Too Short',
    color: 'bg-gray-300'
  });
  // PHASE 3: Email availability check state
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  useEffect(() => {
    setIsVisible(true);
  }, []);
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(formData.password));
  }, [formData.password]);
  // ─── Load Turnstile script once ────────────────────────────────────────
  useEffect(() => {
    if (!ENABLE_SIGNUP_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    if (window.turnstile) return; // already loaded
    if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)) return;
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);
  // ─── Render the Turnstile widget into our container ────────────────────
  useEffect(() => {
    if (!ENABLE_SIGNUP_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const tryRender = () => {
      if (cancelled) return;
      const container = turnstileContainerRef.current;
      if (!container || !window.turnstile) return false;
      if (turnstileWidgetIdRef.current) return true; // already rendered
      try {
        const widgetId = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            if (!cancelled) setTurnstileToken(token);
          },
          'expired-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          'error-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          theme: 'light'
        });
        turnstileWidgetIdRef.current = widgetId;
        return true;
      } catch (err) {
        console.error('[Turnstile] render failed:', err);
        return false;
      }
    };
    if (!tryRender()) {
      intervalId = setInterval(() => {
        if (tryRender() && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 200);
    }
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);
  // PHASE 3: Real-time email validation with debounce
  useEffect(() => {
    if (!formData.email) {
      setEmailAvailable(null);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setEmailAvailable(null);
      return;
    }
    // Debounce email check
    const timeoutId = setTimeout(async () => {
      setEmailChecking(true);
      try {
        // Check if email exists in Firebase Auth
        // Note: This requires a Cloud Function or backend endpoint
        // For now, we'll skip this check
        setEmailAvailable(true);
      } catch (error) {
        console.error('Error checking email:', error);
      } finally {
        setEmailChecking(false);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.email]);
  const calculatePasswordStrength = (password: string) => {
    if (!password || password.length < 1) {
      return {
        score: 0,
        label: 'Too Short',
        color: 'bg-gray-300'
      };
    }
    let score = 0;
    if (password.length >= 10) score += 1;
    if (password.length >= 14) score += 1;
    if (/[a-z]/.test(password)) score += 0.5;
    if (/[A-Z]/.test(password)) score += 0.5;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password)) score += 1;
    score = Math.min(4, score);
    const strengthMap = [{
      score: 0,
      label: 'Too Short',
      color: 'bg-gray-300'
    }, {
      score: 1,
      label: 'Weak',
      color: 'bg-red-500'
    }, {
      score: 2,
      label: 'Fair',
      color: 'bg-orange-500'
    }, {
      score: 3,
      label: 'Good',
      color: 'bg-yellow-500'
    }, {
      score: 4,
      label: 'Strong',
      color: 'bg-green-500'
    }];
    return strengthMap[Math.floor(score)];
  };
  // PHASE 3: Real-time field validation
  const validateField = (fieldName: string, value: string): string => {
    switch (fieldName) {
      case 'firstName':
        if (!value.trim()) return 'First name is required';
        if (value.trim().length < 2) return 'First name must be at least 2 characters';
        return '';
      case 'lastName':
        if (!value.trim()) return 'Last name is required';
        if (value.trim().length < 2) return 'Last name must be at least 2 characters';
        return '';
      case 'email':
        if (!value) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        if (isDisposableEmail(value)) return 'Please use a permanent email address. Temporary/disposable email providers are not allowed.';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 10) return 'Password must be at least 10 characters';
        if (!/[0-9]/.test(value)) return 'Password must include at least one number';
        if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(value)) return 'Password must include at least one special character';
        return '';
      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return '';
      default:
        return '';
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      id,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    // PHASE 3: Real-time validation
    const error = validateField(id, value);
    setFieldErrors(prev => ({
      ...prev,
      [id]: error
    }));
    setErrorMessage('');
  };
  const validateForm = () => {
    const errors = {
      firstName: validateField('firstName', formData.firstName),
      lastName: validateField('lastName', formData.lastName),
      email: validateField('email', formData.email),
      password: validateField('password', formData.password),
      confirmPassword: validateField('confirmPassword', formData.confirmPassword)
    };
    setFieldErrors(errors);
    // Telemetry: if the email rejection was specifically the disposable-email
    // blocklist hit, record it per-IP so we can see throwaway-domain pressure
    // separately from generic format errors.
    if (errors.email && isDisposableEmail(formData.email)) {
      fireSignupTelemetry('signup_disposable_email');
    }
    // Check if any errors exist
    const hasErrors = Object.values(errors).some(error => error !== '');
    if (hasErrors) {
      setErrorMessage('Please fix the errors above');
      return false;
    }
    if (!agreeToTerms) {
      setErrorMessage('You must agree to the terms and conditions');
      return false;
    }
    if (ENABLE_SIGNUP_CAPTCHA && !turnstileToken) {
      setErrorMessage('Please complete the verification challenge before submitting.');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    // ─── Cloudflare Turnstile gate ────────────────────────────────────
    // Verify BEFORE we touch Firebase Auth. If verification fails the
    // signup is aborted and the user can re-verify and resubmit.
    if (ENABLE_SIGNUP_CAPTCHA) {
      if (!turnstileToken) {
        setErrorMessage('Please complete the verification challenge before submitting.');
        setIsSubmitting(false);
        return;
      }
      // Read honeypot value directly from the DOM.
      const honeypotEl = document.getElementById('mc-website-hp') as HTMLInputElement | null;
      const honeypot = honeypotEl?.value ?? '';
      const verifyFn = httpsCallable<{
        token: string;
        honeypot: string;
        flow: 'student';
      }, {
        verified: boolean;
        errors?: string[];
      }>(functions, 'verifyTurnstile');
      let verifyResult;
      try {
        verifyResult = await verifyFn({
          token: turnstileToken,
          honeypot,
          flow: 'student'
        });
      } catch (verifyErr) {
        console.error('[Turnstile] verification call failed:', verifyErr);
        setErrorMessage('Verification could not be completed. Please refresh the challenge and try again.');
        if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        }
        setTurnstileToken(null);
        setIsSubmitting(false);
        return;
      }
      if (!verifyResult.data?.verified) {
        console.warn('[Turnstile] verification rejected:', verifyResult.data?.errors);
        setErrorMessage('Verification failed. Please complete the challenge again and resubmit.');
        if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        }
        setTurnstileToken(null);
        setIsSubmitting(false);
        return;
      }
      // Telemetry breadcrumb — Turnstile passed for this flow. The
      // server-side counter has already recorded `turnstile_verified`.
      Sentry.addBreadcrumb({
        category: 'signup',
        level: 'info',
        message: 'turnstile_verified',
        data: {
          flow: 'student'
        }
      });
    }
    try {
      console.log('Starting student registration process...');
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      await registerStudent(formData.email, formData.password, formData.firstName, formData.lastName);
      console.log('Student registration successful!');
      // Telemetry: account created. Fire-and-forget — never blocks UX.
      fireSignupTelemetry('signup_succeeded');
      setSuccessMessage('Your account has been created! A verification email has been sent to your inbox.');
      localStorage.setItem('studentEmail', formData.email);
      localStorage.setItem('studentName', fullName);
      localStorage.setItem('studentFirstName', formData.firstName);
      localStorage.setItem('studentLastName', formData.lastName);
      await new Promise(resolve => setTimeout(resolve, 1500));
      navigate('/email-verification');
    } catch (error: any) {
      console.error('Error during signup:', error);
      // Telemetry: signup rejected by Firebase Auth (or another downstream
      // failure). error.code is a Firebase Auth code like
      // 'auth/email-already-in-use' — safe to record as a dimension.
      fireSignupTelemetry('signup_auth_error', typeof error?.code === 'string' ? error.code : undefined);
      setErrorMessage(error.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="signup-form" className="py-8 md:py-12 bg-navy-900/5" data-id="element-11714">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-11715">
        <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-11716">
          <div className="bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-lg border border-gray-100 dark:border-slate-700 p-6 md:p-8" data-id="element-11717">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 dark:text-white mb-5 text-center" data-id="element-11718">
              Create Your Student Account
            </h2>
            {successMessage && <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg flex items-start" data-id="element-11719">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-11720" />
                <span data-id="element-11721">{successMessage}</span>
              </div>}
            {errorMessage && <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-start" data-id="element-11722">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-11723" />
                <span data-id="element-11724">{errorMessage}</span>
              </div>}
            <form onSubmit={handleSubmit} data-id="element-11725">
              <div className="space-y-5" data-id="element-11726">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11727">
                  {/* First Name */}
                  <div data-id="element-11728">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11729">
                      First Name
                    </label>
                    <input type="text" id="firstName" value={formData.firstName} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 dark:focus:ring-gray-500 dark:focus:border-gray-500 transition-colors text-gray-900 dark:text-white dark:bg-slate-700 ${fieldErrors.firstName ? 'border-red-400 dark:border-red-400' : 'border-gray-300 dark:border-slate-600'}`} placeholder="Your first name" required disabled={isSubmitting} data-id="element-11730" />
                    {fieldErrors.firstName && <p className="text-red-500 text-xs mt-1" data-id="element-11731">
                        {fieldErrors.firstName}
                      </p>}
                  </div>
                  {/* Last Name */}
                  <div data-id="element-11732">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11733">
                      Last Name
                    </label>
                    <input type="text" id="lastName" value={formData.lastName} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 dark:focus:ring-gray-500 dark:focus:border-gray-500 transition-colors text-gray-900 dark:text-white dark:bg-slate-700 ${fieldErrors.lastName ? 'border-red-400 dark:border-red-400' : 'border-gray-300 dark:border-slate-600'}`} placeholder="Your last name" required disabled={isSubmitting} data-id="element-11734" />
                    {fieldErrors.lastName && <p className="text-red-500 text-xs mt-1" data-id="element-11735">
                        {fieldErrors.lastName}
                      </p>}
                  </div>
                </div>
                {/* Email */}
                <div data-id="element-11736">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11737">
                    Email Address
                  </label>
                  <div className="relative" data-id="element-11738">
                    <input type="email" id="email" value={formData.email} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 dark:focus:ring-gray-500 dark:focus:border-gray-500 transition-colors text-gray-900 dark:text-white dark:bg-slate-700 ${fieldErrors.email ? 'border-red-400 dark:border-red-400' : 'border-gray-300 dark:border-slate-600'}`} placeholder="you@example.com" required disabled={isSubmitting} data-id="element-11739" />
                    {emailChecking && <div className="absolute right-3 top-3" data-id="element-11740">
                        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-11741">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-11742"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-11743"></path>
                        </svg>
                      </div>}
                  </div>
                  {fieldErrors.email && <p className="text-red-500 text-xs mt-1" data-id="element-11744">
                      {fieldErrors.email}
                    </p>}
                </div>
                {/* Password */}
                <div data-id="element-11745">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11746">
                    Password
                  </label>
                  <div className="relative" data-id="element-11747">
                    <input type={passwordVisible ? 'text' : 'password'} id="password" value={formData.password} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 dark:focus:ring-gray-500 dark:focus:border-gray-500 transition-colors text-gray-900 dark:text-white dark:bg-slate-700 ${fieldErrors.password ? 'border-red-400 dark:border-red-400' : 'border-gray-300 dark:border-slate-600'}`} placeholder="Create a secure password" required disabled={isSubmitting} data-id="element-11748" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => setPasswordVisible(!passwordVisible)} disabled={isSubmitting} data-id="element-11749">
                      {passwordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-11750" /> : <EyeIcon className="h-5 w-5" data-id="element-11751" />}
                    </button>
                  </div>
                  {fieldErrors.password && <p className="text-red-500 text-xs mt-1" data-id="element-11752">
                      {fieldErrors.password}
                    </p>}
                  {/* Password Strength Indicator */}
                  {formData.password && <div className="mt-2" data-id="element-11753">
                      <div className="w-full h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden" data-id="element-11754">
                        <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{
                      width: `${passwordStrength.score / 4 * 100}%`
                    }} data-id="element-11755"></div>
                      </div>
                      <div className="mt-1 flex justify-between items-center" data-id="element-11756">
                        <span className={`text-xs font-medium ${passwordStrength.score <= 1 ? 'text-red-500' : passwordStrength.score === 2 ? 'text-orange-500' : passwordStrength.score === 3 ? 'text-yellow-500' : 'text-green-500'}`} data-id="element-11757">
                          {passwordStrength.label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-11758">
                          8+ chars, number, special character
                        </span>
                      </div>
                    </div>}
                </div>
                {/* Confirm Password */}
                <div data-id="element-11759">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11760">
                    Confirm Password
                  </label>
                  <div className="relative" data-id="element-11761">
                    <input type={confirmPasswordVisible ? 'text' : 'password'} id="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 dark:focus:ring-gray-500 dark:focus:border-gray-500 transition-colors text-gray-900 dark:text-white dark:bg-slate-700 ${fieldErrors.confirmPassword ? 'border-red-400 dark:border-red-400' : 'border-gray-300 dark:border-slate-600'}`} placeholder="Confirm your password" required disabled={isSubmitting} data-id="element-11762" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} disabled={isSubmitting} data-id="element-11763">
                      {confirmPasswordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-11764" /> : <EyeIcon className="h-5 w-5" data-id="element-11765" />}
                    </button>
                  </div>
                  {fieldErrors.confirmPassword && <p className="text-red-500 text-xs mt-1" data-id="element-11766">
                      {fieldErrors.confirmPassword}
                    </p>}
                </div>
                {/* ─── Honeypot (visually hidden, real users never fill) ─── */}
                {ENABLE_SIGNUP_CAPTCHA && <div aria-hidden="true" style={{
                position: 'absolute',
                left: '-10000px',
                top: 'auto',
                width: '1px',
                height: '1px',
                overflow: 'hidden'
              }} data-id="element-11767">
                    <label htmlFor="mc-website-hp" data-id="element-11768">Website (leave blank)</label>
                    <input id="mc-website-hp" name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" data-id="element-11769" />
                  </div>}

                {/* ─── Cloudflare Turnstile widget ─── */}
                {ENABLE_SIGNUP_CAPTCHA && <div data-id="element-11770">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-11771">
                      Human Verification
                    </label>
                    <div className="flex justify-center" data-id="element-11772">
                      <div ref={turnstileContainerRef} data-id="element-11773" />
                    </div>
                  </div>}

                {/* Terms Checkbox */}
                <div className="flex items-start" data-id="element-11774">
                  <div className="flex items-center h-5" data-id="element-11775">
                    <input id="terms" name="terms" type="checkbox" checked={agreeToTerms} onChange={() => setAgreeToTerms(!agreeToTerms)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-gray-300 dark:border-slate-600 rounded" required disabled={isSubmitting} data-id="element-11776" />
                  </div>
                  <div className="ml-3 text-sm" data-id="element-11777">
                    <label htmlFor="terms" className="font-medium text-gray-700 dark:text-gray-300" data-id="element-11778">
                      I agree to the{' '}
                      <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-11779">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-11780">
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                </div>
                {/* Submit Button */}
                <button type="submit" disabled={isSubmitting || ENABLE_SIGNUP_CAPTCHA && !turnstileToken} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-11781">
                  {isSubmitting ? <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-11782">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-11783"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-11784"></path>
                      </svg>
                      Creating Account...
                    </> : 'Create Account'}
                </button>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2" data-id="element-11785">
                  Already have an account?{' '}
                  <button type="button" onClick={() => navigate('/student-login')} className="text-blue-600 hover:text-blue-700" disabled={isSubmitting} data-id="element-11786">
                    Sign In
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>;
};